import React, { useMemo, useCallback } from 'react';
import { Tooltip, Button } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import useGraphicStandMutationCreateUpdate from '../hooks/graphic-stand-use-mutation-create-update';
import { GRAPHIC_STAND_STATUS } from '../graphic-stand-const';
import { Localize } from 'components/service';
import { useGraphicStandFields } from 'components/use-hooks';
import { successNotification } from 'components/request-result';
import Icons from 'components/icons';

const GraphicStandCreateButton = ({ stand, graphicPackingId }) => {
    const [ graphicStandId, setGraphicStandId ] = React.useState(null);

    const createOptions = useMemo(() => ({
        variables: {
            graphic_list_id: graphicPackingId
        },
        skipRedirect: true,
        skipNotifications: true
    }), [ graphicPackingId ]);

    const updateOptions = useMemo(() => ({
        graphicStandId: graphicStandId || 'temp',
        variables: {
            graphic_list_id: graphicPackingId
        },
        skipRedirect: true,
        skipNotifications: true
    }), [ graphicStandId, graphicPackingId ]);

    const {
        create,
        loading: createLoading
    } = useGraphicStandMutationCreateUpdate(createOptions);

    const {
        update,
        loading: updateLoading
    } = useGraphicStandMutationCreateUpdate(updateOptions);

    const { getAllFields, areAllFieldsFilled, clearStandFields } = useGraphicStandFields(stand?.id);

    const allFieldsFilled = stand ? areAllFieldsFilled() : false;

    const handleCreateClick = useCallback(() => {
        if (!allFieldsFilled)
        {
            return;
        }

        const { hall, corridor, block, stand: standNumber } = getAllFields();

        const input = {
            exhibitor: stand.exhibitor,
            hall_name: hall,
            block: block,
            stand_number: standNumber,
            corridor: corridor,
            status: GRAPHIC_STAND_STATUS.DONE,
            graphic_list_id: graphicPackingId,
            stand_id: stand.id
        };

        try
        {
            const result = create({
                variables: {
                    input
                }
            });

            if (result && typeof result.then === 'function')
            {
                result.then((response) => {
                    // After successful creation, update the graphic stand
                    if (response?.data?.graphicStandCreate?.graphicStand?.id)
                    {
                        const newGraphicStandId = response.data.graphicStandCreate.graphicStand.id;

                        // Set the ID for update mutation
                        setGraphicStandId(newGraphicStandId);

                        // Update the graphic stand with the variables you specified
                        const updateInput = {
                            id: newGraphicStandId,
                            graphic_list_id: graphicPackingId,
                            status: GRAPHIC_STAND_STATUS.DONE
                        };

                        // Call update mutation
                        update({
                            variables: {
                                input: updateInput
                            }
                        }).then(() => {
                            // Clear all fields after successful update
                            clearStandFields();
                            setGraphicStandId(null); // Reset ID

                            // Show notification of successful completion of both operations
                            successNotification({
                                title: "Grafikstand erstellt",
                                description: "Grafikstand wurde erfolgreich aus dem Lager entnommen"
                            });
                        }).catch(() => {
                            setGraphicStandId(null); // Reset ID on error
                        });
                    } else
                    {
                        // Clear fields even if update is not needed
                        clearStandFields();
                    }
                }).catch(() => {
                    // Error is handled in hook, data remains in fields
                });
            }
        } catch (error)
        {
            // Function call error handling
        }
    }, [ allFieldsFilled, stand, getAllFields, graphicPackingId, create, update, clearStandFields ]);

    const buttonClass = useMemo(() => {
        if (createLoading || updateLoading) return 'create-button-loading';
        if (allFieldsFilled) return 'create-button-active';
        return 'create-button-inactive';
    }, [ createLoading, updateLoading, allFieldsFilled ]);

    const tooltipText = useMemo(() => {
        if (createLoading || updateLoading) return <Localize>ACTIONS.Creating</Localize>;
        if (allFieldsFilled) return <Localize>ACTIONS.Create_Graphic_Stand</Localize>;
        return <Localize>ACTIONS.Fill_All_Fields</Localize>;
    }, [ createLoading, updateLoading, allFieldsFilled ]);

    // Simple check - if no stand, return empty div
    if (!stand)
    {
        return <div>No stand</div>;
    }

    return (
        <div style={ { padding: '15px 22px' } }>
            <Tooltip
                overlayClassName="craft-tooltip"
                destroyTooltipOnHide={ true }
                title={ tooltipText }
            >
                <Button
                    type="primary"
                    className={ `create-button ${ buttonClass }` }
                    onClick={ handleCreateClick }
                    disabled={ !allFieldsFilled || createLoading || updateLoading }
                    loading={ createLoading || updateLoading }
                    // size="small"
                    style={ {
                        backgroundColor: allFieldsFilled ? '#52c41a' : '#7A7F84',
                        borderColor: allFieldsFilled ? '#52c41a' : '#7A7F84',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'black',
                        gap: '2px',
                        height: '33px',
                        padding: '5px',
                        minWidth: '70px',
                        maxWidth: '70px',
                        width: '70px',
                    } }
                >
                    <Icons.TruckArrow style={ { fontSize: '30px', lineHeight: 1 } } />
                    <ArrowRightOutlined style={ { fontSize: '18px', lineHeight: 1 } } />
                </Button>
            </Tooltip>
        </div>
    );
};

export default GraphicStandCreateButton;
